/**
 * @fileoverview 日付ユーティリティ util
 */
'use strict';
// Library Import
import _ from 'lodash';
import moment from 'moment';
// Util Import
import * as ConstUtil from './ConstUtil';

/**
 * 日時のフォーマット変換
 *
 * @name UnixTimeToDateString
 * @function
 * @memberOf utils.DateUtil
 * @param {Number} unixTime UnixTime
 * @param {String} tokens 出力フォーマット
 * @return {String} unixTimeを指定フォーマットに変換した文字列
 */
export const UnixTimeToDateString = (unixTime, tokens) => {
  // NaNチェック
  if (_.isNaN(unixTime)) {
    return '';
  }
  // 数値チェック
  if (!_.isNumber(unixTime)) {
    return '';
  }
  // 文字列チェック
  if (!_.isString(tokens)) {
    return '';
  }
  moment.locale('ja');
  moment.updateLocale('ja', {
    weekdaysShort: [
      ConstUtil.Message.COMMON.ID515,
      ConstUtil.Message.COMMON.ID509,
      ConstUtil.Message.COMMON.ID510,
      ConstUtil.Message.COMMON.ID511,
      ConstUtil.Message.COMMON.ID512,
      ConstUtil.Message.COMMON.ID513,
      ConstUtil.Message.COMMON.ID514
    ]
  });
  return moment(unixTime).format(tokens);
};

/**
 * 日付文字列からDateオブジェクト生成
 *
 * @param {String} dateString 日付文字列
 * @returns {Date} Dateオブジェクト
 */
export const StringToDate = (dateString, tokens) => {
  // 文字列チェック
  if (!_.isString(dateString)) {
    return '';
  }
  moment.locale('ja');
  return moment(dateString, tokens).toDate();
};

/**
 * 日時の加算
 *
 * @name AddDate
 * @function
 * @memberOf utils.DateUtil
 * @param {Date} date 日付
 * @param {String} displayPattern 日付のパターン
 * @param {Boolean} isAdd 加算か減算かを判定する値
 * @return {Date} dateを計算した値
 */
export const AddDate = (date, displayPattern, isAdd) => {
  // Dateチェック
  if (!_.isDate(date)) {
    return date;
  }
  // 日付のパターンチェック
  let pattern = _.findKey(ConstUtil.Common.MOMENT_KEYS, function(key) {
    if (displayPattern.indexOf(key) !== -1) {
      return key;
    }
  });
  if (_.isEmpty(pattern)) {
    return displayPattern;
  }
  // trueの場合は+1
  if (isAdd) {
    return moment(date)
      .add(1, displayPattern)
      .toDate();
  }
  // falseの場合は-1
  return moment(date)
    .add(-1, displayPattern)
    .toDate();
};

/**
 * 時間単位ごとの最初の時間を取得
 *
 * @param {String} timeUnit 時間単位
 */
export const GetStartDate = timeUnit => {
  // 文字列でない場合
  if (!_.isString(timeUnit)) {
    return;
  }
  return moment()
    .startOf(timeUnit)
    .toDate();
};

/**
 * 時間加算	* 時間単位ごとの最初の時間を取得（日付を指定）
 * 住友理工カスタマイズ 三日分の予約マスを表示させるため日付を指定する必要がある。
 *
 * @param {String} targetDate 日付
 * @param {String} timeUnit 時間単位
 */
export const GetStartTargetDate = (targetDate, timeUnit) => {
  // 時間単位が文字列でない場合
  if (!_.isString(timeUnit)) {
    return;
  }
  return moment(targetDate)
    .startOf(timeUnit)
    .toDate();
};

/**
 * 時間加算
 *
 * @param {Date} targetDate 対象日付
 * @param {Number} number 加算する数値
 * @param {String} timeUnit 時間単位
 * @returns {Date} 加算結果
 */
export const AddTime = (targetDate, number, timeUnit) => {
  // 日付でない場合
  if (!_.isDate(targetDate)) {
    return;
  }
  // 数値でない場合
  if (!_.isNumber(number)) {
    return;
  }
  // 文字列でない場合
  if (!_.isString(timeUnit)) {
    return;
  }
  return moment(targetDate)
    .add(number, timeUnit)
    .toDate();
};

/**
 * 比較元より前の日付かどうかチェックする
 *
 * @param {Date} targetDate 比較対象
 * @param {Date} sourceDate 比較元
 * @param {String} timeUnit 時間単位
 */
export const isBefore = (targetDate, sourceDate, timeUnit) => {
  // 日付でない場合
  if (!_.isDate(targetDate)) {
    return;
  }
  // 日付でない場合
  if (!_.isDate(sourceDate)) {
    return;
  }
  // 文字列でない場合
  if (!_.isString(timeUnit)) {
    return;
  }
  return moment(targetDate).isBefore(sourceDate, timeUnit);
};

/**
 * 比較元より前or同じ日付かどうかチェックする
 *
 * @param {Date} targetDate 比較対象
 * @param {Date} sourceDate 比較元
 * @param {String} timeUnit 時間単位
 */
export const isSameOrBefore = (targetDate, sourceDate, timeUnit) => {
  // 日付でない場合
  if (!_.isDate(targetDate)) {
    return;
  }
  // 日付でない場合
  if (!_.isDate(sourceDate)) {
    return;
  }
  // 文字列でない場合
  if (!_.isString(timeUnit)) {
    return;
  }
  return moment(targetDate).isSameOrBefore(sourceDate, timeUnit);
};

/**
 * 日付文字列を別のフォーマットに置きかえた文字列を返す
 *
 * @param {String} beforeDateString
 * @param {String} token
 * @returns {String} 置き換え後の日付文字列
 */
export const changeDateFormat = (beforeDateString, token) => {
  if (_.isEmpty(beforeDateString)) {
    return ConstUtil.Message.COMMON.ID502;
  }
  if (_.isEmpty(token)) {
    return ConstUtil.Message.COMMON.ID502;
  }
  // Dateに変換
  let beforeDate = StringToDate(beforeDateString);
  if (!_.isDate(beforeDate)) {
    return ConstUtil.Message.COMMON.ID502;
  }
  // Stringに変換
  return UnixTimeToDateString(beforeDate.getTime(), token);
};

/**
 * 日時の差分
 *
 * @param {Date} targetDate
 * @param {Date} sourceDate
 * @param {String} timeUnit
 */
export const differenceDate = (targetDate, sourceDate, timeUnit) => {
  // 日付でない場合
  if (!_.isDate(targetDate)) {
    return;
  }
  // 日付でない場合
  if (!_.isDate(sourceDate)) {
    return;
  }
  // 文字列でない場合
  if (!_.isString(timeUnit)) {
    return;
  }
  return moment(targetDate).diff(moment(sourceDate), timeUnit);
};

/**
 * 日時の差分(HH:mmで返却)
 *
 * @param {Date} targetDate
 * @param {Date} sourceDate
 * @return {String} HH:mm
 */
export const differenceHourMinutes = (targetDate, sourceDate) => {
  // 日付でない場合
  if (!_.isDate(targetDate)) {
    return ConstUtil.Message.COMMON.ID502;
  }
  // 日付でない場合
  if (!_.isDate(sourceDate)) {
    return ConstUtil.Message.COMMON.ID502;
  }

  const diffMinutes = Math.abs(
    differenceDate(targetDate, sourceDate, ConstUtil.Common.MOMENT_KEYS.MINUTES)
  );
  const hours = ('00' + Math.floor(diffMinutes / 60)).slice(-2);
  const minutes = ('00' + (diffMinutes % 60)).slice(-2);
  return `${hours}:${minutes}`;
};

/**
 * 分切り上げ時刻取得
 *
 * @param {Date} targetTime 変換対象日時
 * @param {Number} minutes 切り上げる分
 * @return {Date} 5分切り上げ後の日時
 */
export const getRoundUpMinutes = (targetTime, minutes) => {
  // //時・分を取得する
  let targetMinutes = targetTime.getMinutes();
  // 5分切り上げ後の数値
  const roundedUp = Math.ceil(targetMinutes / minutes) * minutes;
  // 切り上げ後との差分を日時に追加する
  return AddTime(
    targetTime,
    roundedUp - targetMinutes,
    ConstUtil.Common.MOMENT_KEYS.MINUTES
  );
};

/**
 * 開始～終了が指定期間内かチェックする
 *
 * @param {Date} startDate 開始日
 * @param {Date} endDate 終了日
 * @param {Number} period 期間数
 * @param {String} displayPattern 計算用日付のパターン
 * @param {String} compareDisplayPattern 比較用日付のパターン
 * @returns {Boolean} True:期間内, False:期間外
 */
export const IsWithinPeriod = (
  startDate,
  endDate,
  period,
  displayPattern,
  compareDisplayPattern
) => {
  // Dateチェック
  if (!_.isDate(startDate)) {
    return startDate;
  }
  if (!_.isDate(endDate)) {
    return endDate;
  }
  // Numberチェック
  if (!_.isNumber(period)) {
    return period;
  }
  // 日付のパターンチェック
  let pattern = _.findKey(ConstUtil.Common.MOMENT_KEYS, function(key) {
    if (displayPattern.indexOf(key) !== -1) {
      return key;
    }
  });
  if (_.isEmpty(pattern)) {
    return displayPattern;
  }
  // 終了日から期間数を減算
  let substractedDate = moment(endDate)
    .subtract(period, displayPattern)
    .toDate();
  // 開始日 >= 終了日の月-1
  return moment(startDate).isSameOrAfter(
    substractedDate,
    compareDisplayPattern
  );
};



// WEBPACK FOOTER //
// ./src/js/util/DateUtil.jsx